/**
 * SplashScreen
 *
 * Flow:
 * 1. Mount: fetch data, show loader until splash image + lottie are ready
 * 2. Ready: fade out loader, reveal splash image as fullscreen bg with overlay content + button
 * 3. Button click: remove splash image, play lottie to completion
 * 4. Lottie complete: crossfade into WelcomeSequence
 *
 * Debug: set SKIP_INTRO to true to jump straight to WelcomeSequence
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { DotLottie } from "@lottiefiles/dotlottie-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import introAnimation from "@/assets/lottie/intro.lottie";

import { useCacheStore } from "@/state/cacheStore";
import { useCacheStatus, useGameConfiguration, useLegal, usePhases } from "@/state/cacheStoreHooks";
import { useGameStore } from "@/state/gameStore";
import { useFullscreen } from "@/hooks/useFullscreen";
import DevToolsButton from "@/components/screens/SplashScreen/components/DevToolsButton";
import WelcomeSequence from "@/components/screens/SplashScreen/components/IntroSequence/components/WelcomeSequence/WelcomeSequence";
import IngLogo from "@/components/logos/IngLogo";
import Button from "@/components/form/Button";
import ArrowRightIcon from "@/components/icons/ArrowRightIcon";
import { StrapiMedia } from "@/components/shared/StrapiMedia";
import { useAppVersionCheck } from "@/hooks/useAppVersionCheck";
import LoaderIcon from "@/components/icons/LoaderIcon";
import { IS_DEV_ENV } from "@/constants/envConstants";
import SplashSubLine from "@/components/screens/SplashScreen/components/SplashSubLine/SplashSubLine";
import { Message } from "@/types/api";

// ─── Debug flag: skip intro and go straight to WelcomeSequence ───
const SKIP_INTRO = false;

type Props = {
    onDevToolsClick: () => void;
};

type Phase = "idle" | "intro" | "transition" | "welcome";

export function SplashScreen({ onDevToolsClick }: Props) {
    const [phase, setPhase] = useState<Phase>(SKIP_INTRO ? "welcome" : "idle");
    const [hasClickedStart, setHasClickedStart] = useState(SKIP_INTRO);
    const [splashImageLoaded, setSplashImageLoaded] = useState(SKIP_INTRO);
    const [lottieLoaded, setLottieLoaded] = useState(SKIP_INTRO);
    const lottieRef = useRef<DotLottie | null>(null);
    const legal = useLegal();
    const { status, error } = useCacheStatus();
    const gameConfig = useGameConfiguration();
    const phases = usePhases();
    const fetchData = useCacheStore((state) => state.fetchData);
    const startPhase = useGameStore((state) => state.startPhase);
    const setMessageQueueTimerDuration = useGameStore((state) => state.setQueueTimerDuration);
    
    const { request: requestFullscreen } = useFullscreen();

    // If no splash image is configured, treat it as loaded
    const hasSplashImage = !!gameConfig?.SplashImage;
    const effectiveSplashImageLoaded = splashImageLoaded || !hasSplashImage;

    const ready = effectiveSplashImageLoaded && lottieLoaded;

    // Check app version on mount, will automatically reload if a new version is detected
    useAppVersionCheck();

    // ─── Data ready → start first game phase ───
    useEffect(() => {
        if (!hasClickedStart) return;
        if (status !== "success") return;
        if (phases.length === 0) return;

        const firstPhase = phases[0];
        startPhase(firstPhase.documentId, firstPhase.messages.filter((m): m is Message => m != null));
    }, [hasClickedStart, status, phases, startPhase]);

    // ─── Lottie ref + event listeners ───
    const onLottieRef = useCallback((instance: DotLottie | null) => {
        if (!instance) return;
        lottieRef.current = instance;

        instance.addEventListener("load", () => {
            setLottieLoaded(true);
        });

        instance.addEventListener("complete", () => {
            setPhase("transition");
        });

        // Guard against race: instance may already be loaded by the time listeners are attached
        if (instance.isLoaded) {
            setLottieLoaded(true);
        }
    }, []);

    // ─── Start button handler ───
    const handleStart = (disableMessageDelay = false) => {
        requestFullscreen().finally(() => {
            if (disableMessageDelay) {
                setMessageQueueTimerDuration(0);
            }
            setHasClickedStart(true);
            setPhase("intro");
            setTimeout(() => {
                if (lottieRef.current) {
                    lottieRef.current.play();
                }
            }, 500);
        });
    };

    const handleRetry = () => {
        fetchData();
    };

    // ─── Error state ───
    if (status === "error") {
        return (
            <div className="flex size-full flex-col items-center justify-center gap-4 p-4">
                <h1 className="text-2xl font-bold text-red-600">Failed to load game</h1>
                <p className="text-gray-600">{error?.message ?? "Unknown error"}</p>
                <button onClick={handleRetry} className="rounded bg-blue-500 px-6 py-2 text-white hover:bg-blue-600">
                    Retry
                </button>
            </div>
        );
    }

    const showLoader = !ready;
    const showSplashImage = phase === "idle";
    const showOverlay = phase === "idle" && ready;
    const showWelcome = phase === "transition" || phase === "welcome";

    return (
        <div className="relative size-full bg-secondary-500">
            {/* Layer 0: Fullscreen loader — visible until splash image + lottie are ready */}
            <AnimatePresence>
                {showLoader && (
                    <motion.div
                        key="loader"
                        className="absolute inset-0 z-30 flex items-center justify-center bg-secondary-500"
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                        <LoaderIcon />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Layer 1: Lottie — loads in background, plays on start */}
            <AnimatePresence onExitComplete={() => setPhase("welcome")}>
                {phase !== "welcome" && (
                    <motion.div
                        key="lottie-bg"
                        className="absolute inset-0"
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                        <DotLottieReact
                            src={introAnimation}
                            autoplay={false}
                            loop={false}
                            layout={{ fit: "cover" }}
                            dotLottieRefCallback={onLottieRef}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Layer 2: Splash image — covers lottie during idle, removed instantly on start */}
            {showSplashImage && gameConfig?.SplashImage && (
                <div className="absolute inset-0 z-10">
                    <StrapiMedia
                        size="lg"
                        image={gameConfig.SplashImage}
                        className="size-full"
                        objectFit="cover"
                        priority
                        useImageAspectRatio={false}
                        onLoad={() => setSplashImageLoaded(true)}
                    />
                </div>
            )}

            {/* Layer 3: Overlay content — fades out on start */}
            <AnimatePresence>
                {showOverlay && (
                    <motion.div
                        key="overlay"
                        className="absolute inset-0 z-20 flex flex-col items-center justify-end p-4"
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                        <SplashSubLine/>
                        <Button theme="secondary" className="mt-0.5" content="between" onClick={() => handleStart(false)}>
                            <span className="text-base">Spiel starten</span>
                            <ArrowRightIcon />
                        </Button>
                        <div className="flex justify-center font-light tracking-tighter text-base gap-x-2 items-center my-[10px]">
                            <span>In Kooperation mit </span>
                            <div className="mb-[3px]">
                                <IngLogo />
                            </div>
                        </div>
                        <div className="flex justify-center  tracking-tighter text-xs gap-x-1.5 items-baseline underline">
                            <a href={legal?.Impressum ?? "#"} target="_blank" rel="noopener noreferrer">
                                Impressum
                            </a>
                            <a href={legal?.Datenschutz ?? "#"} target="_blank" rel="noopener noreferrer">
                                Datenschutz
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Layer 4: WelcomeSequence — fades in during transition */}
            <AnimatePresence>
                {showWelcome && (
                    <motion.div
                        key="welcome"
                        className="absolute inset-0"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                        <WelcomeSequence />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Dev mode overlay */}
            {IS_DEV_ENV && gameConfig?.DebugMode === true && phase === "idle" && <DevToolsButton onClick={onDevToolsClick} />}
        </div>
    );
}